import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, Trash2, CheckCircle, Flag, ExternalLink, Building2, Inbox, Bug, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AdminCompanies from "@/components/AdminCompanies";
import AdminLeads from "@/components/AdminLeads";
import AdminBugReports from "@/components/AdminBugReports";
import AdminLegalAlerts from "@/components/AdminLegalAlerts";

export default function Admin() {
  const { user, setAuthOpen } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/flagged");
      setItems(data);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
      else if (e.response?.status === 401) setAuthOpen(true);
    }
  }, [setAuthOpen]);

  useEffect(() => {
    if (user === false) { setAuthOpen(true); return; }
    if (user) load();
  }, [user, load]); // eslint-disable-line

  const remove = async (rid) => {
    await api.delete(`/admin/reviews/${rid}`);
    toast.success("Review removed");
    setItems((it) => it.filter((r) => r.id !== rid));
  };

  const dismiss = async (rid) => {
    await api.post(`/admin/reviews/${rid}/dismiss`);
    toast.success("Flag dismissed — review kept");
    setItems((it) => it.filter((r) => r.id !== rid));
  };

  if (denied || (user && user.role !== "admin")) {
    return <div className="max-w-3xl mx-auto px-8 py-20 text-center text-slate-500" data-testid="admin-denied">Admin access required.</div>;
  }
  if (!user) return <div className="max-w-7xl mx-auto px-8 py-20 text-slate-400 font-mono">Loading...</div>;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
      <p className="font-mono text-xs uppercase tracking-widest text-rose-600 mb-2">Admin panel</p>
      <h1 className="font-display text-5xl text-slate-900 mb-6 flex items-center gap-3" style={{ fontWeight: 900 }}>
        <ShieldAlert className="text-rose-600" /> Admin Console
      </h1>

      <Tabs defaultValue="moderation">
        <TabsList className="mb-6">
          <TabsTrigger value="moderation" data-testid="admin-tab-moderation">
            <Flag size={14} className="mr-1.5" /> Moderation{items && items.length > 0 ? ` (${items.length})` : ""}
          </TabsTrigger>
          <TabsTrigger value="companies" data-testid="admin-tab-companies">
            <Building2 size={14} className="mr-1.5" /> Companies
          </TabsTrigger>
          <TabsTrigger value="leads" data-testid="admin-tab-leads">
            <Inbox size={14} className="mr-1.5" /> Leads
          </TabsTrigger>
          <TabsTrigger value="bugs" data-testid="admin-tab-bugs">
            <Bug size={14} className="mr-1.5" /> Bug Reports
          </TabsTrigger>
          <TabsTrigger value="legal" data-testid="admin-tab-legal">
            <Scale size={14} className="mr-1.5" /> Legal Alerts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="moderation">
          <p className="text-slate-500 mb-5">Reviews flagged by employees. Remove ones that break the rules, or dismiss the flag to keep them.</p>
          {!items ? (
            <p className="text-slate-400 font-mono text-sm">Loading...</p>
          ) : items.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-16 text-center" data-testid="moderation-empty">
              <CheckCircle className="mx-auto text-emerald-500 mb-3" size={40} />
              <p className="text-slate-500">All clear — no flagged reviews right now.</p>
            </div>
          ) : (
            <div className="space-y-5" data-testid="flagged-list">
              {items.map((r) => (
                <div key={r.id} data-testid="flagged-review-card" className="bg-white border border-rose-200 rounded-xl p-6">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-rose-600 bg-rose-50 border border-rose-200 rounded-full px-2.5 py-1">
                        <Flag size={12} /> {r.reports.length} report{r.reports.length === 1 ? "" : "s"}
                      </span>
                      <h3 className="font-display text-xl text-slate-900 mt-2" style={{ fontWeight: 700 }}>{r.title}</h3>
                      <button onClick={() => navigate(`/supervisor/${r.supervisor_id}`)} className="text-sm text-slate-500 hover:text-emerald-700 flex items-center gap-1">
                        on {r.supervisor_name} · {r.company} <ExternalLink size={12} />
                      </button>
                    </div>
                  </div>

                  <p className="text-slate-700 mt-3 leading-relaxed border-l-2 border-slate-200 pl-3">{r.comment}</p>

                  <div className="mt-4 space-y-1.5">
                    {r.reports.map((rep, i) => (
                      <div key={i} className="text-sm text-slate-500 bg-slate-50 rounded-md px-3 py-2">
                        <span className="font-medium text-slate-700">{rep.reason}</span>
                        {rep.details && <span> — {rep.details}</span>}
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-3 mt-5">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button data-testid={`moderation-remove-${r.id}`} variant="destructive" className="gap-2 rounded-full">
                          <Trash2 size={15} /> Remove Review
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove this review?</AlertDialogTitle>
                          <AlertDialogDescription>This permanently deletes the review and recalculates the supervisor's scores.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction data-testid={`moderation-confirm-remove-${r.id}`} onClick={() => remove(r.id)} className="bg-rose-600 hover:bg-rose-700">Remove</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <Button data-testid={`moderation-dismiss-${r.id}`} onClick={() => dismiss(r.id)} variant="outline" className="gap-2 rounded-full">
                      <CheckCircle size={15} /> Keep & Dismiss Flag
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="companies">
          <p className="text-slate-500 mb-5">Add, edit, or remove companies in the directory. Renaming a company updates all its supervisors.</p>
          <AdminCompanies />
        </TabsContent>

        <TabsContent value="leads">
          <p className="text-slate-500 mb-5">Demo requests from the For Employers page. Each one is also emailed to founder@my360boss.com.</p>
          <AdminLeads />
        </TabsContent>

        <TabsContent value="bugs">
          <p className="text-slate-500 mb-5">Anonymous bug reports submitted from the Vault help section. No reporter identity is stored.</p>
          <AdminBugReports />
        </TabsContent>

        <TabsContent value="legal">
          <p className="text-slate-500 mb-5">High-risk emails (takedown, defamation, libel, lawsuit, cease and desist) arriving at support@my360boss.com. A Section 230 reply is saved as a Draft for your review — nothing is sent automatically.</p>
          <AdminLegalAlerts />
        </TabsContent>
      </Tabs>
    </div>
  );
}
